import React from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert
} from '@mui/material';
import { useTlbCycleOptions } from '../components/hooks/useTlbCycleOptions';

/**
 * Per-schedule TLB cycle counter (same keys as component TSR unit Cycles).
 * @param {{ aircraftId: string, currentUsageTotals: object|null, value: string, onChange: (key: string) => void, disabled?: boolean, dialogOpen?: boolean, size?: 'small'|'medium' }} props
 */
export default function ScheduleTlbCycleCounterField({
  aircraftId,
  currentUsageTotals = null,
  value = '',
  onChange,
  disabled = false,
  dialogOpen = true,
  size = 'small'
}) {
  const { tlbCycleOptions, tlbCycleOptionsLoading } = useTlbCycleOptions({
    open: dialogOpen && !!aircraftId,
    aircraftId,
    currentUsageTotals
  });

  if (!aircraftId) {
    return (
      <Alert severity="info" sx={{ py: 0.5 }}>
        Select an aircraft first to choose which TLB cycle counter this schedule uses.
      </Alert>
    );
  }

  const selected = value ? tlbCycleOptions.find((o) => o.key === value) : null;

  return (
    <Box>
      <FormControl fullWidth size={size} disabled={disabled || tlbCycleOptionsLoading}>
        <InputLabel id="schedule-tlb-cycle-label">TLB cycle counter</InputLabel>
        <Select
          labelId="schedule-tlb-cycle-label"
          label="TLB cycle counter"
          displayEmpty
          value={value || ''}
          onChange={(e) => onChange(e.target.value || '')}
          renderValue={(selectedKey) => {
            if (!selectedKey) {
              return (
                <Typography component="span" variant="body2" color="text.secondary">
                  {tlbCycleOptionsLoading ? 'Loading counters…' : 'Select counter (optional)'}
                </Typography>
              );
            }
            const opt = tlbCycleOptions.find((o) => o.key === selectedKey);
            if (!opt) return selectedKey;
            return `${opt.label} — ${opt.liveDisplay}`;
          }}
        >
          <MenuItem value="">
            <em>Use tenant default (Live TLB mapping → Cycles)</em>
          </MenuItem>
          {tlbCycleOptions.map((opt) => (
            <MenuItem key={opt.key} value={opt.key}>
              {opt.label} — {opt.liveDisplay}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {selected && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          TLB live (carried): {selected.liveDisplay}
        </Typography>
      )}
      {!value && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Leave unset to use Client Settings → Live TLB usage mapping for Cycles.
        </Typography>
      )}
    </Box>
  );
}
